import { colors } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { css } from 'styled-components';

import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { isDefined } from 'common/utils';

import { FullSelection, Option } from './pages/SharedMenuEditorPage/types';
import { DraftStatus, SelectionState, SharedMenu } from './types';

export const getVenuesLinkedToSharedMenu = (linkedVenueIds: string[] | null, venues: Venue[]) => {
  if (!linkedVenueIds) {
    return [];
  }

  return linkedVenueIds.map(id => venues.find(venue => venue.id === id)).filter(isDefined);
};

export const isSharedMenuBootstrapping = (draft: DraftStatus) =>
  draft?.bootstrapping?.status === 'pending' || draft?.bootstrapping?.status === 'running';

export const isSharedMenuBootstrappingFailure = (draft: DraftStatus) => draft?.bootstrapping?.status === 'failure';

export const isNewSelection = (selection?: SharedMenu) => selection?.version.number === 1;

/**
 * Returns a typed array of keys from an object since `Object.keys` doesn't.
 *
 * `const myCoolObj: Record<MyCoolType, any> = { ... }`
 *
 * `const keys = getTypedKeys(myCoolObj)`
 *
 * => `keys` will be of type `MyCoolType[]`
 *
 * `const keys = Object.keys(myCoolObj)`
 *
 * => `keys` will be of type `string[]`
 */
export const getTypedKeys = <T extends Record<string, unknown>>(object: T): (keyof T)[] => Object.keys(object);

export const getSelectionState = (selection: SharedMenu): SelectionState => {
  if (isSharedMenuBootstrapping(selection.draft)) {
    return 'bootstrapping';
  }

  if (isNewSelection(selection)) {
    return 'draft';
  }

  if (!!selection.draft && !selection.draft.publish_at) {
    return 'pending-changes';
  }

  if (!!selection.draft?.publish_at) {
    return 'scheduled-changes';
  }

  return 'live';
};

export const checkIfSelectionCanBePublished = (selection: SharedMenu) => {
  const bootstrappingFailed = selection.draft?.bootstrapping?.status === 'failure';
  const bootstrappingInProgress = isSharedMenuBootstrapping(selection.draft);
  return !!selection.draft && !bootstrappingFailed && !bootstrappingInProgress;
};

export const selectionHasValidLastPublishedValue = (selection: SharedMenu) =>
  !isNewSelection(selection) && selection.version.updated_at;

export const getSelectionWithWoltSpecialsAsFirstCategory = (data: FullSelection): FullSelection => {
  const woltSpecialsCategory = data.selection.categories.find(c => !!c.is_wolt_special);

  if (!woltSpecialsCategory) {
    return data;
  }

  const categoriesWithoutWoltSpecials = data.selection.categories.filter(c => !c.is_wolt_special);

  const categories = [woltSpecialsCategory, ...categoriesWithoutWoltSpecials];

  return {
    offers: data.offers,
    options: data.options,
    selection: {
      ...data.selection,
      categories,
    },
  };
};

export const getSafeOptions = (options: Option[] = []) => {
  return options.map(option => ({
    ...option,
    values: option.values.map(value => ({ ...value, price: value.price ?? 0 })),
  }));
};

export const CustomDashedBorderCss = css<{ isActive: boolean; borderRadius: number }>`
  background-image: ${({ borderRadius, isActive, theme }) => {
    const strokeColor = isActive
      ? colors.wolt100
      : cssThemedValue({ light: colors.pepper20, dark: colors.salt24 })({ theme });

    return `url(\"data:image/svg+xml;charset=utf8,
    <svg xmlns='http://www.w3.org/2000/svg' height='100%' width='100%'>
      <rect
        width='100%'
        height='100%'
        fill='none'
        rx='${borderRadius}'
        ry='${borderRadius}'
        stroke='${strokeColor}'
        stroke-width='4'
        stroke-dasharray='8, 5'
        stroke-dashoffset='0'
        stroke-linecap='butt'
      />
    </svg>
  \")`;
  }};
`;

export const getErrorResponseText = (error: any): string => {
  if (!error) {
    return '<missing error data>';
  }

  const data = error.response?.data;

  /**
   * The error response from the Offering Platform is always like below:
   * data class ErrorResponse(
          val title: String,
          val status: Int,
          val detail: String,
      )
   */
  if (data && data.detail && typeof data.detail === 'string') {
    return data.detail;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return JSON.stringify(error);
};
